import { Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { RtkQueryTable, RtkQueryTableModels } from '../../../../components/Table/RtkQueryTable';
import {
  TREASURY_FIRST_PAGE,
  TREASURY_GRID_HEIGHT,
  TREASURY_LIST_PAGE_SIZE,
  TREASURY_RUN_STATUS_COLORS,
} from '../../../../constants/treasury';
import { useFetchTreasuryRedeemAutomationRunsQuery } from '../../../../redux/features/treasury/treasury.apiSlice';
import { TreasuryRedeemAutomationRun } from '../../../../types/treasury';
import { getTreasuryErrorMessage } from '../../../../utils/treasury';

type TreasuryRedeemRunsDialogProps = {
  companyId: number;
  /** The rule whose firings to show; null keeps the dialog closed. */
  ruleId: number | null;
  onClose: () => void;
};

const COLUMNS: GridColDef<TreasuryRedeemAutomationRun>[] = [
  {
    field: 'scheduledFor',
    headerName: 'Scheduled For',
    width: 130,
    sortable: false,
    valueFormatter: ({ value }) => (value ? moment(value).format('MM/DD/YYYY') : ''),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    sortable: false,
    renderCell: ({ row }) => (
      <Chip size="small" label={row.status} color={TREASURY_RUN_STATUS_COLORS[row.status] ?? 'default'} />
    ),
  },
  { field: 'requestType', headerName: 'Type', width: 100, sortable: false },
  { field: 'requestedAmount', headerName: 'Amount (SAR)', width: 130, sortable: false },
  {
    field: 'contributionRedeemRequestId',
    headerName: 'Redeem Request',
    width: 140,
    sortable: false,
    valueFormatter: ({ value }) => (value ? `#${value}` : '—'),
  },
  { field: 'attempt', headerName: 'Attempt', width: 90, sortable: false },
  {
    field: 'errorMessage',
    headerName: 'Error',
    width: 240,
    sortable: false,
    valueFormatter: ({ value }) => value ?? '—',
  },
];

/** Every firing of one rule — what it asked for, how it ended and the redeem request it made. */
export const TreasuryRedeemRunsDialog = ({ companyId, ruleId, onClose }: TreasuryRedeemRunsDialogProps) => {
  const { enqueueSnackbar } = useSnackbar();
  // thriftplan answers every run at once, so the grid pages them itself.
  const [tableModels, setTableModels] = useState<RtkQueryTableModels>({ paginationModel: TREASURY_FIRST_PAGE });
  const { data, isFetching, isError, error } = useFetchTreasuryRedeemAutomationRunsQuery(
    { companyId, id: ruleId ?? 0 },
    { skip: ruleId === null },
  );

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(getTreasuryErrorMessage(error, 'Failed to load the runs'), { variant: 'error' });
    }
  }, [isError, error, enqueueSnackbar]);

  return (
    <Dialog open={ruleId !== null} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Rule #{ruleId} — Runs</DialogTitle>
      <DialogContent>
        {ruleId !== null && (
          <RtkQueryTable
            key={ruleId}
            models={tableModels}
            onModelsChange={setTableModels}
            rows={data ?? []}
            columns={COLUMNS}
            loading={isFetching}
            paginationMode="client"
            pageSizeOptions={[TREASURY_LIST_PAGE_SIZE]}
            disableColumnFilter
            wrapperHeight={TREASURY_GRID_HEIGHT}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
